import Decimal from "decimal.js";

/**
 * Estimate the liquidation price for a perpetual position.
 *
 * @param markPrice Current mark price as a string.
 * @param leverage  Position leverage (must be > 1 for a meaningful result).
 * @param side      Position side: "long" or "short".
 * @returns Estimated liquidation price as a Decimal, or null if inputs are invalid.
 */
export function estimateLiquidationPrice(
  markPrice: string | null | undefined,
  leverage: number,
  side: "long" | "short",
): Decimal | null {
  if (!markPrice || leverage <= 1) return null;

  const mark = new Decimal(markPrice);
  const leverageFactor = new Decimal(1).div(leverage);

  if (side === "long") {
    // Long liq ~ mark x (1 - 1/leverage)
    return mark.mul(new Decimal(1).minus(leverageFactor));
  }
  // Short liq ~ mark x (1 + 1/leverage)
  return mark.mul(new Decimal(1).plus(leverageFactor));
}

interface DnEstimationMinNotional {
  legAMinNotionalUsdc?: string | null;
  legBMinNotionalUsdc?: string | null;
}

/**
 * Compute a capital validation error for a single DN leg.
 *
 * Returns a user-facing error string when the notional (capital x leverage) is below
 * the exchange's minimum notional, or null when valid.
 */
export function computeCapitalError(
  estimation: DnEstimationMinNotional | null | undefined,
  capital: string,
  leverage: number,
  leg: "A" | "B",
): string | null {
  const minNotionalRaw = leg === "A" ? estimation?.legAMinNotionalUsdc : estimation?.legBMinNotionalUsdc;
  if (!minNotionalRaw || !capital || !leverage) return null;
  const notional = new Decimal(capital || "0").mul(leverage);
  const minNotional = new Decimal(minNotionalRaw);
  if (notional.lt(minNotional)) {
    const minCapital = minNotional.div(leverage).ceil().toString();
    return `Min capital: $${minCapital} at ${leverage}x leverage`;
  }
  return null;
}
